import React, { useState } from "react";
import { Card, Form, Button, Row, Col, Alert } from "react-bootstrap";
import API from "../services/api";

const GuardVehicleLookup = () => {
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [details, setDetails] = useState(null);
  const [form, setForm] = useState({
    actualQuantity: "",
    unit: "",
    materialCondition: "",
    remarks: "",
    timeIn: "",
    timeOut: "",
    status: "verified",
    sealObserved: "",
    grossWeight: "",
    grossSlip: "",
    numberPlatePhoto: "",
    vehicleFrontPhoto: "",
    loadedMaterialPhoto: "",
    location: null,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleLookup = async () => {
    setMessage("");
    if (!vehicleNumber.trim()) {
      setMessage("Enter a vehicle number.");
      return;
    }
    setLoading(true);
    try {
      const res = await API.get("/guard/vehicle-requests/lookup", {
        params: vehicleNumber.trim().toUpperCase().startsWith("GP-")
          ? { gatePassCode: vehicleNumber.trim() }
          : { vehicleNumber },
      });
      setDetails(res.data);
      setMessage("");
    } catch (err) {
      setDetails(null);
      setMessage(err.response?.data?.message || "No approved request found.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!details?.request?._id) return;
    setLoading(true);
    setMessage("");
    try {
      await API.post(`/guard/vehicle-requests/${details.request._id}/verify`, {
        ...form,
        actualQuantity: form.actualQuantity ? Number(form.actualQuantity) : undefined,
        grossWeight: form.grossWeight ? Number(form.grossWeight) : undefined,
      });
      setMessage("Verification saved and admin notified.");
    } catch (err) {
      setMessage(err.response?.data?.message || "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const request = details?.request;
  const fileData = (field, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((prev) => ({ ...prev, [field]: reader.result }));
    reader.readAsDataURL(file);
  };

  const captureLocation = () => {
    if (!navigator.geolocation) return setMessage("Location is not supported on this device.");
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((prev) => ({ ...prev, location: { lat: pos.coords.latitude, lng: pos.coords.longitude } })),
      () => setMessage("Location permission is required for secure gate verification.")
    );
  };

  return (
    <div>
      <Card className="shadow-sm mb-3">
        <Card.Body>
          <Card.Title>Vehicle Lookup</Card.Title>
          <Card.Text>Enter vehicle number or scan/type the Gate Pass code.</Card.Text>
          <Row className="g-2 align-items-center">
            <Col md={8}>
              <Form.Control
                type="text"
                placeholder="UP14AB1234 or GP-..."
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
              />
            </Col>
            <Col md={4}>
              <Button variant="success" onClick={handleLookup} disabled={loading} className="w-100">
                Fetch
              </Button>
            </Col>
          </Row>
          {message && <Alert variant="info" className="mt-3">{message}</Alert>}
        </Card.Body>
      </Card>

      {request && (
        <Row className="g-3">
          <Col lg={6}>
            <Card className="shadow-sm">
              <Card.Header className="fw-bold">Approved Details</Card.Header>
              <Card.Body>
                <p className="mb-1"><strong>Vehicle:</strong> {request.vehicleNumber}</p>
                <p className="mb-1"><strong>Gate Pass:</strong> {request.gatePassCode || "Legacy"}</p>
                <p className="mb-1"><strong>Vendor:</strong> {request.vendorName}</p>
                <p className="mb-1"><strong>Material:</strong> {request.materialName || "N/A"}</p>
                <p className="mb-1"><strong>Type:</strong> {request.materialType}</p>
                <p className="mb-1">
                  <strong>Qty:</strong> {request.quantity || 0} {request.unit || ""}
                </p>
                <p className="mb-1">
                  <strong>Expected:</strong>{" "}
                  {request.expectedTime
                    ? new Date(request.expectedTime).toLocaleString()
                    : "Not set"}
                </p>
                <p className="mb-1">
                  <strong>Challan/Invoice:</strong> {request.challanInvoiceNumber || "N/A"}
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="shadow-sm">
              <Card.Header className="fw-bold">Verify & Confirm</Card.Header>
              <Card.Body>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Control
                      type="number"
                      placeholder="Actual Quantity"
                      value={form.actualQuantity}
                      onChange={(e) => setForm({ ...form, actualQuantity: e.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Control type="text" placeholder="Observed Seal Number" value={form.sealObserved} onChange={(e) => setForm({ ...form, sealObserved: e.target.value.toUpperCase() })} />
                  </Col>
                  <Col md={6}>
                    <Form.Control type="number" min="0" step="0.01" placeholder="Gross Weighbridge Weight" value={form.grossWeight} onChange={(e) => setForm({ ...form, grossWeight: e.target.value })} />
                  </Col>
                  <Col md={6}>
                    <Form.Control type="text" placeholder="Gross Slip Number/URL" value={form.grossSlip} onChange={(e) => setForm({ ...form, grossSlip: e.target.value })} />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">Number Plate Photo</Form.Label>
                    <Form.Control type="file" accept="image/*" capture="environment" onChange={(e) => fileData("numberPlatePhoto", e.target.files?.[0])} />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">Vehicle Front Photo</Form.Label>
                    <Form.Control type="file" accept="image/*" capture="environment" onChange={(e) => fileData("vehicleFrontPhoto", e.target.files?.[0])} />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small">Loaded Material Photo</Form.Label>
                    <Form.Control type="file" accept="image/*" capture="environment" onChange={(e) => fileData("loadedMaterialPhoto", e.target.files?.[0])} />
                  </Col>
                  <Col md={12}>
                    <Button type="button" variant={form.location ? "outline-success" : "outline-secondary"} size="sm" onClick={captureLocation}>
                      {form.location ? "Location Captured" : "Capture Gate Location"}
                    </Button>
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      type="text"
                      placeholder="Unit (kg/mt)"
                      value={form.unit}
                      onChange={(e) => setForm({ ...form, unit: e.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      type="text"
                      placeholder="Material Condition"
                      value={form.materialCondition}
                      onChange={(e) => setForm({ ...form, materialCondition: e.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                    >
                      <option value="arrived">Arrived</option>
                      <option value="verified">Verified</option>
                      <option value="exited">Exited</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      type="datetime-local"
                      value={form.timeIn}
                      onChange={(e) => setForm({ ...form, timeIn: e.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      type="datetime-local"
                      value={form.timeOut}
                      onChange={(e) => setForm({ ...form, timeOut: e.target.value })}
                    />
                  </Col>
                </Row>
                <Form.Control
                  as="textarea"
                  rows={3}
                  className="mt-2"
                  placeholder="Remarks"
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                />
                <Button
                  variant="success"
                  className="w-100 mt-3"
                  onClick={handleVerify}
                  disabled={loading}
                >
                  Confirm Verification
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default GuardVehicleLookup;
